import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createSupabaseServerClient } from "./ads.server";
import { AD_LIFETIME_DAYS, type AdStatRow, type DailyStatPoint } from "./ad-stats";

const TrackInput = z.object({
  slug: z.string().min(1).max(64),
  kind: z.enum(["impression", "view", "contact", "share"]),
  visitorId: z.string().max(64).optional(),
});

/** Registrerar en händelse (visning, klick, kontakt) på en annons. Publik. */
export const trackAdEvent = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => TrackInput.parse(input))
  .handler(async ({ data }) => {
    const supabase = createSupabaseServerClient();
    const { data: ad } = await supabase
      .from("ads")
      .select("id")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!ad) return { ok: false };
    await supabase.from("ad_events").insert({
      ad_id: ad.id,
      kind: data.kind,
      visitor_id: (data.visitorId ?? "").slice(0, 64),
    });
    return { ok: true };
  });

/** Statistik + rekommendationer för den inloggades alla annonser. */
export const getMyAdStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data: ads, error } = await supabase
      .from("ads")
      .select("id, slug, title, price, category, created_at, expires_at, is_paused, promoted_until")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    if (!ads?.length) return { ads: [] as AdStatRow[], lifetimeDays: AD_LIFETIME_DAYS };

    const ids = ads.map((a) => a.id);

    const [{ data: events }, { data: msgs }] = await Promise.all([
      supabase.from("ad_events").select("ad_id, kind, visitor_id, created_at").in("ad_id", ids),
      supabase.from("messages").select("ad_id, sender_id, created_at").in("ad_id", ids),
    ]);

    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

    const rows: AdStatRow[] = ads.map((ad) => {
      const own = (events ?? []).filter((e) => e.ad_id === ad.id);
      const views = own.filter((e) => e.kind === "view");
      const uniq = new Set(views.map((e) => e.visitor_id).filter(Boolean));
      const inbox = (msgs ?? []).filter((m) => m.ad_id === ad.id && m.sender_id !== userId);

      return {
        slug: ad.slug,
        title: ad.title,
        price: ad.price,
        category: ad.category,
        created_at: ad.created_at,
        expires_at: ad.expires_at,
        is_paused: ad.is_paused,
        promoted_until: ad.promoted_until,
        impressions: own.filter((e) => e.kind === "impression").length,
        views: views.length,
        uniqueViews: uniq.size || views.length,
        viewsLast7: views.filter((e) => new Date(e.created_at).getTime() > weekAgo).length,
        contacts: inbox.length,
        shares: own.filter((e) => e.kind === "share").length,
      };
    });

    return { ads: rows, lifetimeDays: AD_LIFETIME_DAYS };
  });

/** Ger annonsen 60 nya dagar och tar bort en automatisk paus. */
export const renewAd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1).max(64) }).parse(input))
  .handler(async ({ data, context }) => {
    const expires = new Date(Date.now() + AD_LIFETIME_DAYS * 24 * 60 * 60 * 1000).toISOString();
    const { data: updated, error } = await context.supabase
      .from("ads")
      .update({ expires_at: expires, is_paused: false })
      .eq("slug", data.slug)
      .eq("user_id", context.userId)
      .select("slug, expires_at, is_paused")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!updated) throw new Error("Annonsen hittades inte");
    return updated;
  });

/**
 * Dag-för-dag-statistik för en annons (visningar, unika besökare, kontakter).
 * Ingår i Plus och Pro – gratiskonton får ett tips om att uppgradera.
 */
export const getAdDailyStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({ slug: z.string().min(1).max(64), days: z.number().int().min(7).max(90).optional() })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { readPlanAndUsage } = await import("./plans.server");
    const { plan } = await readPlanAndUsage(context.supabase as never, context.userId);
    if (plan === "free") {
      return { locked: true as const, plan, days: [] as DailyStatPoint[] };
    }

    const days = data.days ?? 30;
    const { data: ad } = await context.supabase
      .from("ads")
      .select("id")
      .eq("slug", data.slug)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (!ad) throw new Error("Annonsen hittades inte eller tillhör inte dig.");

    const since = new Date(Date.now() - (days - 1) * 86_400_000);
    since.setHours(0, 0, 0, 0);
    const sinceIso = since.toISOString();

    const [{ data: events }, { data: msgs }] = await Promise.all([
      context.supabase
        .from("ad_events")
        .select("kind, visitor_id, created_at")
        .eq("ad_id", (ad as { id: string }).id)
        .gte("created_at", sinceIso),
      context.supabase
        .from("messages")
        .select("sender_id, created_at")
        .eq("ad_id", (ad as { id: string }).id)
        .gte("created_at", sinceIso),
    ]);

    const buckets = new Map<
      string,
      { views: number; visitors: Set<string>; contacts: number; shares: number }
    >();
    for (let i = 0; i < days; i++) {
      const d = new Date(since.getTime() + i * 86_400_000);
      buckets.set(dayKey(d), { views: 0, visitors: new Set(), contacts: 0, shares: 0 });
    }

    for (const e of events ?? []) {
      const b = buckets.get(dayKey(new Date(e.created_at as string)));
      if (!b) continue;
      if (e.kind === "view") {
        b.views += 1;
        if (e.visitor_id) b.visitors.add(e.visitor_id as string);
      } else if (e.kind === "share") {
        b.shares += 1;
      }
    }
    for (const m of msgs ?? []) {
      if (m.sender_id === context.userId) continue;
      const b = buckets.get(dayKey(new Date(m.created_at as string)));
      if (b) b.contacts += 1;
    }

    const points: DailyStatPoint[] = Array.from(buckets, ([date, b]) => ({
      date,
      views: b.views,
      uniqueViews: b.visitors.size || b.views,
      contacts: b.contacts,
      shares: b.shares,
    }));

    return { locked: false as const, plan, days: points };
  });

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
